// path: src/Profile.js

import React, { useMemo, useState } from "react";
import "./App.css";
import characters from "./Characters.json";
import { isUsernameAllowed } from "./utils/usernameBlocklist";
import { getCardImageSrc } from "./utils/cardImages";
import { formatGid, isValidGid } from "./utils/gid";
import { getAvatarFrameClassName } from "./utils/cosmetics";
import AvatarFrameDecor from "./Components/cosmetics/AvatarFrameDecor";
import NameBadgeChip from "./Components/cosmetics/NameBadgeChip";
import { BOOST_CATALOG } from "./utils/boosts";
import { isBoostInventoryItemId } from "./utils/inventory";
import BoostIcon from "./Components/boosts/BoostIcon";
import BoostHelpModal from "./Components/boosts/BoostHelpModal";

const normalizeId = (id) => {
    const n = Number(id);
    return Number.isFinite(n) ? n : id;
};

const getImgSrc = (card) => getCardImageSrc(card);

const Profile = ({
    username = "",
    onChangeUsername,
    unlockedIds = [],
    selectedProfileImageId = null,
    equippedCosmetics = null,
    onCosmetics,
    onCollection,
    onChooseProfileImage,
    onAchievements,
    onFriends,
    inventoryEntries = [],
    onOpenCorgiPacks,
    cardsOwned = 0,
    gamesPlayed = 0,
    foilsFound = 0,
    treats = 0,
    highestScore = 0,
    highestLevel = 0,
    longestStreak = 0,
    isAuthenticated = false,
    gid = null,
    gidStatus = "idle",
    gidError = "",
    onRetryGid,
}) => {
    const [draftUsername, setDraftUsername] = useState(username);
    const [editingUsername, setEditingUsername] = useState(false);
    const [usernameError, setUsernameError] = useState("");
    const [copiedGid, setCopiedGid] = useState(false);
    const [boostHelpEntry, setBoostHelpEntry] = useState(null);
    const hasGid = isAuthenticated && isValidGid(gid);
    const gidText = hasGid ? formatGid(gid) : "";

    const inventoryBoostEntries = useMemo(() => {
        const byId = new Map(
            (inventoryEntries || []).map((entry) => [entry.id, entry])
        );
        return BOOST_CATALOG.map((boost) => {
            const owned = byId.get(boost.id);
            return {
                id: boost.id,
                name: boost.name,
                description: boost.description,
                icon: boost.icon,
                iconSrc: boost.iconSrc,
                count: owned ? owned.count : 0,
            };
        });
    }, [inventoryEntries]);

    const nonBoostInventoryEntries = useMemo(
        () =>
            (inventoryEntries || []).filter(
                (entry) => !isBoostInventoryItemId(entry.id)
            ),
        [inventoryEntries]
    );

    const handleCopyGid = async () => {
        if (!gidText || !navigator?.clipboard?.writeText) {
            return;
        }

        try {
            await navigator.clipboard.writeText(gidText);
            setCopiedGid(true);
            window.setTimeout(() => setCopiedGid(false), 1600);
        } catch (error) {
            console.error("Failed to copy GID:", error);
        }
    };

    const unlockedSet = useMemo(() => {
        return new Set((unlockedIds || []).map(normalizeId));
    }, [unlockedIds]);

    const unlockedCards = useMemo(() => {
        return characters.filter((card) => unlockedSet.has(normalizeId(card.id)));
    }, [unlockedSet]);

    const selectedProfileCard = useMemo(() => {
        const normalizedSelectedId = normalizeId(selectedProfileImageId);
        const fromEquippedAvatar = equippedCosmetics?.avatar?.cardId;

        return (
            unlockedCards.find(
                (card) => normalizeId(card.id) === normalizedSelectedId
            ) ||
            unlockedCards.find(
                (card) => normalizeId(card.id) === normalizeId(fromEquippedAvatar)
            ) ||
            unlockedCards[0] ||
            null
        );
    }, [selectedProfileImageId, unlockedCards, equippedCosmetics]);

    const equipped = equippedCosmetics || {};

    const handleStartEditingUsername = () => {
        setDraftUsername(username);
        setUsernameError("");
        setEditingUsername(true);
    };

    const handleSaveUsername = () => {
        const cleaned = draftUsername.trim();

        if (!cleaned) {
            setUsernameError("Username cannot be empty.");
            return;
        }

        if (!isUsernameAllowed(cleaned)) {
            setUsernameError("That username is not allowed.");
            return;
        }

        if (onChangeUsername) {
            const saved = onChangeUsername(cleaned);

            if (saved === false) {
                setUsernameError("That username is not allowed.");
                return;
            }
        }

        setUsernameError("");
        setEditingUsername(false);
    };

    return (
        <div className="profileScreen">
            <div className="profileHeader">
                <h1 className="impactFont pageTitle profileTitle">Profile</h1>

                <div className="profileHeaderActions">
                    {!editingUsername ? (
                        <button
                            type="button"
                            className="profileUsernameEdit"
                            onClick={handleStartEditingUsername}
                            aria-label="Edit username"
                            title="Edit username"
                        >
                            <svg
                                className="profileUsernameEdit__icon"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                                focusable="false"
                            >
                                <path
                                    fill="currentColor"
                                    d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                                />
                            </svg>
                        </button>
                    ) : null}
                </div>
            </div>

            <div className="profilePanel">
                <section className="profileHero" aria-label="Profile identity">
                    <button
                        type="button"
                        className={[
                            "profileImageFrame",
                            typeof onChooseProfileImage === "function"
                                ? "profileImageFrame--picker"
                                : "",
                            getAvatarFrameClassName(equipped.avatarFrame),
                            !equipped.avatarFrame
                                ? "profileImageFrame--bare"
                                : "",
                        ]
                            .filter(Boolean)
                            .join(" ")}
                        onClick={
                            typeof onChooseProfileImage === "function"
                                ? onChooseProfileImage
                                : undefined
                        }
                        aria-label="Choose profile picture from Collection"
                    >
                        <div className="profileImageFrame__media">
                            {selectedProfileCard ? (
                                <img
                                    src={getImgSrc(selectedProfileCard)}
                                    alt=""
                                    className="profileImage"
                                />
                            ) : (
                                <div className="profileImagePlaceholder">
                                    Unlock a card, then tap here to choose it from Collection.
                                </div>
                            )}
                        </div>
                        <AvatarFrameDecor
                            visualKey={equipped.avatarFrame?.visualKey}
                        />
                    </button>

                    <div className="profileHeroIdentity">
                        <div className="profileUsernameBlock">
                            {!editingUsername ? (
                                <>
                                    <div className="profileUsernameRow">
                                        <div className="profileUsername">{username}</div>
                                        <NameBadgeChip badge={equipped.nameBadge} />
                                    </div>
                                    {equipped.title?.name ? (
                                        <div className="profileEquippedTitle">
                                            {equipped.title.name}
                                        </div>
                                    ) : null}
                                </>
                            ) : (
                                <>
                                    <input
                                        className="profileUsernameInput"
                                        value={draftUsername}
                                        onChange={(event) =>
                                            setDraftUsername(event.target.value)
                                        }
                                        maxLength={24}
                                        aria-label="Username"
                                        autoFocus
                                    />

                                    <div className="profileUsernameActions">
                                        <button
                                            type="button"
                                            className="profileSmallButton"
                                            onClick={handleSaveUsername}
                                        >
                                            Save
                                        </button>

                                        <button
                                            type="button"
                                            className="profileSmallButton profileSmallButton--secondary"
                                            onClick={() => {
                                                setUsernameError("");
                                                setEditingUsername(false);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>

                                    {usernameError && (
                                        <div className="profileUsernameError">
                                            {usernameError}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="profileGidBlock">
                            {isAuthenticated ? (
                                hasGid ? (
                                    <>
                                        <div className="profileGidLabel">
                                            Game Identity (GID)
                                        </div>
                                        <div className="profileGidRow">
                                            <div className="profileGidValue">
                                                {gidText}
                                            </div>
                                            <button
                                                type="button"
                                                className="profileSmallButton"
                                                onClick={handleCopyGid}
                                            >
                                                {copiedGid ? "Copied" : "Copy"}
                                            </button>
                                        </div>
                                    </>
                                ) : gidStatus === "loading" ? (
                                    <div className="profileGidPending">
                                        Setting up your GID…
                                    </div>
                                ) : (
                                    <div className="profileGidPending">
                                        <div>
                                            {gidError ||
                                                "GID is not ready yet. Tap Try Again."}
                                        </div>
                                        {typeof onRetryGid === "function" && (
                                            <button
                                                type="button"
                                                className="profileSmallButton"
                                                onClick={onRetryGid}
                                                style={{ marginTop: 8 }}
                                            >
                                                Try Again
                                            </button>
                                        )}
                                    </div>
                                )
                            ) : (
                                <div className="profileGidPending">
                                    Sign in to get a GID and use Friends.
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                <nav className="profileButtons" aria-label="Profile destinations">
                    <button className="homePrimaryButton" onClick={onCollection}>
                        Collection
                    </button>

                    <button className="homePrimaryButton" onClick={onAchievements}>
                        Achievements
                    </button>

                    {typeof onCosmetics === "function" ? (
                        <button
                            type="button"
                            className="homePrimaryButton"
                            onClick={onCosmetics}
                        >
                            Cosmetics
                        </button>
                    ) : null}

                    <button className="homePrimaryButton" onClick={onFriends}>
                        Friends
                    </button>
                </nav>

                <section className="profileInventoryPanel" aria-label="Inventory">
                    <h2 className="profileSectionTitle">Inventory</h2>
                    <div className="profileBoostGrid" aria-label="Boosts">
                        {inventoryBoostEntries.map((entry) => (
                            <button
                                key={entry.id}
                                type="button"
                                className={`profileBoostCell profileBoostCell--button${
                                    entry.count > 0
                                        ? ""
                                        : " profileBoostCell--empty"
                                }`}
                                onClick={() => setBoostHelpEntry(entry)}
                                aria-label={`${entry.name}: ${entry.description}. Owned: ${entry.count}.`}
                            >
                                <span
                                    className="profileBoostCell__icon"
                                    aria-hidden="true"
                                >
                                    <BoostIcon boost={entry} size={36} />
                                </span>
                                <span className="profileBoostCell__count">
                                    {entry.count}
                                </span>
                                <span className="profileBoostCell__name">
                                    {entry.name}
                                </span>
                            </button>
                        ))}
                    </div>
                    {nonBoostInventoryEntries.length === 0 ? (
                        <p className="profileInventoryEmpty">
                            No packs yet. Corgi Packs you earn or buy can be
                            saved here to open later.
                        </p>
                    ) : (
                        <ul className="profileInventoryList">
                            {nonBoostInventoryEntries.map((entry) => (
                                <li
                                    key={entry.id}
                                    className="profileInventoryItem"
                                >
                                    <div className="profileInventoryItem__copy">
                                        <div className="profileInventoryItem__title">
                                            {entry.label}
                                        </div>
                                        {entry.description ? (
                                            <div className="profileInventoryItem__desc">
                                                {entry.description}
                                            </div>
                                        ) : null}
                                    </div>
                                    {entry.id === "corgi_pack" &&
                                    typeof onOpenCorgiPacks === "function" ? (
                                        <div className="profileInventoryItem__actions">
                                            <button
                                                type="button"
                                                className="profileSmallButton"
                                                onClick={() =>
                                                    onOpenCorgiPacks(1)
                                                }
                                            >
                                                Open 1
                                            </button>
                                            {entry.count > 1 ? (
                                                <button
                                                    type="button"
                                                    className="profileSmallButton"
                                                    onClick={() =>
                                                        onOpenCorgiPacks(
                                                            entry.count
                                                        )
                                                    }
                                                >
                                                    Open all
                                                </button>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="profileStatsPanel" aria-label="Historical stats">
                    <h2 className="profileSectionTitle">Historical Stats</h2>

                    <div className="profileStats">
                        <div className="profileStat">
                            <div className="profileStatLabel">Games Played</div>
                            <div className="profileStatValue">{gamesPlayed}</div>
                        </div>

                        <div className="profileStat">
                            <div className="profileStatLabel">Cards Owned</div>
                            <div className="profileStatValue">{cardsOwned}</div>
                        </div>

                        <div className="profileStat">
                            <div className="profileStatLabel">Foils Found</div>
                            <div className="profileStatValue">{foilsFound}</div>
                        </div>

                        <div className="profileStat">
                            <div className="profileStatLabel">Treats</div>
                            <div className="profileStatValue">
                                {Number(treats).toLocaleString("en-US")}
                            </div>
                        </div>

                        <div className="profileStat">
                            <div className="profileStatLabel">Highest Score</div>
                            <div className="profileStatValue">{highestScore}</div>
                        </div>

                        <div className="profileStat">
                            <div className="profileStatLabel">Highest Level</div>
                            <div className="profileStatValue">{highestLevel}</div>
                        </div>

                        <div className="profileStat">
                            <div className="profileStatLabel">Longest Streak</div>
                            <div className="profileStatValue">{longestStreak}</div>
                        </div>
                    </div>
                </section>
            </div>

            {boostHelpEntry ? (
                <BoostHelpModal
                    boost={boostHelpEntry}
                    ownedCount={boostHelpEntry.count}
                    onClose={() => setBoostHelpEntry(null)}
                />
            ) : null}
        </div>
    );
};

export default Profile;
